/**
 * RulesTab Component
 *
 * Clean key-value rule reference organized by billing workflow:
 * "Before You Bill" | "When You Bill" | "After You Bill"
 */

import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import type { PayerContract, Citation } from '../types';
import { CitationChip } from './CitationChip';

// ─── Rule Row Helper ─────────────────────────────────────────────────────────

interface RuleRow {
  label: string;
  value: string | null | undefined;
  citation?: Citation;
  highlight?: boolean;
}

const RuleSection: React.FC<{
  title: string;
  rows: RuleRow[];
  payer: PayerContract;
}> = ({ title, rows, payer }) => {
  const theme = useTheme();
  const visibleRows = rows.filter(r => r.value != null && r.value !== '');

  if (visibleRows.length === 0) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', letterSpacing: 1.5, mb: 1, display: 'block' }}
      >
        {title}
      </Typography>
      <Table size="small">
        <TableBody>
          {visibleRows.map((row, idx) => (
            <TableRow
              key={idx}
              sx={{
                '&:last-child td': { border: 0 },
                '&:hover': { bgcolor: theme.palette.action.hover },
              }}
            >
              <TableCell
                sx={{
                  width: '30%',
                  color: 'text.secondary',
                  fontWeight: 500,
                  py: 1.25,
                  verticalAlign: 'top',
                  border: 'none',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                {row.label}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: row.highlight ? 600 : 400,
                  color: row.highlight ? 'error.main' : 'text.primary',
                  py: 1.25,
                  verticalAlign: 'top',
                  border: 'none',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                {row.value}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  width: 180,
                  py: 1.25,
                  verticalAlign: 'top',
                  border: 'none',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                {row.citation && (
                  <CitationChip citation={row.citation} documentRegistry={payer.document_registry} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

// ─── Build Rules from Payer Data ─────────────────────────────────────────────

function getSourceCitation(payer: PayerContract, docId: string, section?: string, pages?: string): Citation {
  return { doc_id: docId, section: section || null, pages: pages || null };
}

function buildBeforeYouBillRules(payer: PayerContract): RuleRow[] {
  const auth = payer.authorization;
  const rows: RuleRow[] = [
    {
      label: 'Prior auth required?',
      value: auth.prior_auth_required ? `Yes \u2014 ${auth.prior_auth_scope}` : `No \u2014 ${auth.prior_auth_scope}`,
      citation: auth._source ? (Array.isArray(auth._source) ? auth._source[0] : auth._source) : undefined,
    },
  ];

  if (auth.auth_codes_required && auth.auth_codes_required.length > 0) {
    rows.push({
      label: 'Codes requiring auth',
      value: auth.auth_codes_required.join(', '),
    });
  }
  if (auth.auth_codes_exempt && auth.auth_codes_exempt.length > 0) {
    rows.push({
      label: 'Codes exempt from auth',
      value: auth.auth_codes_exempt.join(', '),
    });
  }
  if (auth.auth_codes_call_to_verify && auth.auth_codes_call_to_verify.length > 0) {
    rows.push({
      label: 'Call to verify',
      value: auth.auth_codes_call_to_verify.join(', '),
    });
  }
  if (auth.retrospective_auth_allowed != null) {
    rows.push({
      label: 'Retrospective auth',
      value: auth.retrospective_auth_allowed
        ? `Allowed${auth.retrospective_auth_window_days ? ` (${auth.retrospective_auth_window_days} day window)` : ''}`
        : 'Not allowed',
    });
  }
  if (auth.visit_limits) {
    rows.push({ label: 'Visit limits', value: auth.visit_limits, highlight: true });
  }
  if (auth.reauth_cadence) {
    rows.push({ label: 'Reauthorization cadence', value: auth.reauth_cadence });
  }
  if (auth.medicare_auth_override) {
    rows.push({ label: 'Medicare auth override', value: auth.medicare_auth_override, highlight: true });
  }
  return rows;
}

function buildWhenYouBillRules(payer: PayerContract): RuleRow[] {
  const claims = payer.claims_submission;
  const rows: RuleRow[] = [
    {
      label: 'Claim form',
      value: claims.claim_form.replace(/_/g, ' ').toUpperCase(),
    },
  ];

  if (claims.telehealth_rules) {
    const th = claims.telehealth_rules;
    if (th.pos_requirement) {
      rows.push({ label: 'Telehealth POS', value: `POS ${th.pos_requirement}`,
        citation: th._source ? (Array.isArray(th._source) ? th._source[0] : th._source) : undefined });
    }
    if (th.modifier_requirement) {
      rows.push({ label: 'Telehealth modifier', value: `Modifier ${th.modifier_requirement}` });
    }
    if (th.allowed_modalities && th.allowed_modalities.length > 0) {
      rows.push({ label: 'Allowed modalities', value: th.allowed_modalities.join(', ') });
    }
    if (th.audio_only_policy) {
      rows.push({ label: 'Audio-only policy', value: th.audio_only_policy });
    }
    if (th.consent_documentation_required != null) {
      rows.push({ label: 'Consent documentation', value: th.consent_documentation_required ? 'Required' : 'Not required' });
    }
  }
  if (claims.modifier_requirements) {
    const mod = claims.modifier_requirements;
    if (mod.place_of_service_rules) {
      rows.push({ label: 'POS rules', value: mod.place_of_service_rules });
    }
  }
  if (claims.required_data_elements && claims.required_data_elements.length > 0) {
    rows.push({ label: 'Required data elements', value: claims.required_data_elements.join(', ') });
  }
  return rows;
}

function buildAfterYouBillRules(payer: PayerContract): RuleRow[] {
  const claims = payer.claims_submission;
  const disputes = payer.disputes_and_appeals;
  const compliance = payer.compliance;

  const rows: RuleRow[] = [
    {
      label: 'Timely filing',
      value: claims.timely_filing_effective_note
        ? `${claims.timely_filing_days} days (${claims.timely_filing_effective_note})`
        : `${claims.timely_filing_days} days`,
      highlight: claims.timely_filing_days <= 90,
      citation: claims._source ? (Array.isArray(claims._source) ? claims._source[0] : claims._source) : undefined,
    },
  ];

  if (claims.timely_filing_cob_secondary_days) {
    rows.push({
      label: 'COB secondary filing',
      value: `${claims.timely_filing_cob_secondary_days} days${claims.timely_filing_cob_secondary_note ? ` \u2014 ${claims.timely_filing_cob_secondary_note}` : ''}`,
    });
  }
  if (claims.corrected_claim_window_days) {
    rows.push({ label: 'Corrected claim window', value: `${claims.corrected_claim_window_days} days` });
  }
  if (claims.corrected_claim_instructions) {
    rows.push({ label: 'Corrected claim process', value: claims.corrected_claim_instructions });
  }
  if (disputes) {
    if (disputes.appeal_deadline_days) {
      rows.push({
        label: 'Appeal deadline',
        value: `${disputes.appeal_deadline_days} days${disputes.appeal_deadline_note ? ` \u2014 ${disputes.appeal_deadline_note}` : ''}`,
        citation: disputes._source ? (Array.isArray(disputes._source) ? disputes._source[0] : disputes._source) : undefined,
      });
    }
    if (disputes.appeal_level_details && disputes.appeal_level_details.length > 0) {
      disputes.appeal_level_details.forEach(level => {
        rows.push({
          label: `Appeal level ${level.level}`,
          value: `${level.deadline_days} days (${level.deadline_basis})`,
        });
      });
    }
    if (disputes.binding_arbitration) {
      rows.push({
        label: 'Binding arbitration',
        value: disputes.arbitration_body ? `Yes \u2014 ${disputes.arbitration_body}` : 'Yes',
      });
    }
  }
  if (compliance) {
    if (compliance.overpayment_return_days) {
      rows.push({ label: 'Overpayment return', value: `${compliance.overpayment_return_days} days`, highlight: compliance.overpayment_return_days <= 30 });
    }
    if (compliance.overpayment_recovery_method) {
      rows.push({ label: 'Recovery method', value: compliance.overpayment_recovery_method });
    }
  }
  return rows;
}

// ─── Main Export ─────────────────────────────────────────────────────────────

interface RulesTabProps {
  payer: PayerContract;
}

export const RulesTab: React.FC<RulesTabProps> = ({ payer }) => {
  return (
    <Box>
      <RuleSection title="Before You Bill" rows={buildBeforeYouBillRules(payer)} payer={payer} />
      <RuleSection title="When You Bill" rows={buildWhenYouBillRules(payer)} payer={payer} />
      <RuleSection title="After You Bill" rows={buildAfterYouBillRules(payer)} payer={payer} />
    </Box>
  );
};
